import React, { Fragment, Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { useFormik } from 'formik';
import { TextField, Select, RadioGroup, Section } from 'components/shared';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { ResellerForm } from './ResellerForm';

const StyledForm = styled.form`
  legend {
    margin-bottom: 1rem;
  }
`;

export default function AccountType(props) {
  const formik = useFormik({ initialValues: props.initialFormState });
  const [validationErrors, setValidationErrors] = React.useState(
    props.initialValidationErrors || {}
  );

  React.useEffect(() => {
    // Clear out affected inputs when switching from subsidiary to another account type
    if (formik.values.value !== 'subsidiary') {
      ['corporateOwnerSelection', 'unlistedCorporateOwnerName'].forEach(
        (attribute) =>
          formik.values[attribute] !== '' && formik.setFieldValue(attribute, '')
      );
    }
  }, [formik.values.value]);

  React.useEffect(() => {
    // Clear out unlistedCorporateOwnerName when switching from other to a corporation
    if (formik.values.corporateOwnerSelection !== 'other') {
      formik.values.unlistedCorporateOwnerName !== '' &&
        formik.setFieldValue('unlistedCorporateOwnerName', '');
    }
  }, [formik.values.corporateOwnerSelection]);

  React.useEffect(() => {
    // Clear validation errors when navigating back to a valid form state
    // This prevents stale validation errors from blocking the Next button
    if (formik.values.value && Object.keys(validationErrors).length > 0) {
      setValidationErrors({});
    }
  }, [formik.values.value]);

  const hasInvalidValue = (attribute) =>
    validationErrors.hasOwnProperty(attribute);
  const showErrors = (attribute) =>
    !!formik.touched[attribute] && hasInvalidValue(attribute);
  const errorText = (attribute) =>
    showErrors(attribute) ? validationErrors[attribute].join(' ') : '';

  const [loading, setLoading] = React.useState(false);

  const saveForm = (attributes) => {
    return props.onSave(attributes).then(({ validationErrors }) => {
      setValidationErrors(validationErrors);
      return Promise.resolve({ validationErrors });
    });
  };

  // Use { leading: true } option in order to return a promise from the debounced function
  const persistFormState = React.useMemo(
    () => debounce(saveForm, 300, { leading: true }),
    []
  );

  const handleChange = (e) => {
    formik.handleChange(e);
    persistFormState({ ...formik.values, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    saveForm(formik.values).then(
      ({ validationErrors: latestValidationErrors }) => {
        const touched = Object.keys(formik.values).reduce((touched, key) => {
          touched[key] = true;
          return touched;
        }, {});

        formik.setTouched(touched).then(() => {
          if (Object.keys(latestValidationErrors).length === 0) {
            props.goToNextPage();
          }
        });
      }
    );
  };

  const componentList = [
    <RadioGroup
      sectionComponentSize="large"
      name="value"
      value={formik.values.value || ''}
      formLabel="What type of account should we create for you?"
      onChange={handleChange}
      errorMessage={errorText('value')}
      error={showErrors('value')}
      radioList={[
        {
          value: 'personal',
          label:
            'Personal account (for your personal orders only, not associated with your clinic)',
        },
        {
          value: 'single_vet_practice',
          label: 'Single veterinarian practice',
        },
        {
          value: 'multi_vet_practice',
          label: 'Multiple veterinarian practice',
        },
        {
          value: 'subsidiary',
          label: 'Subsidiary of a large corporate veterinary practice',
        },
      ]}
    />,
  ];

  if (formik.values.value === 'subsidiary') {
    componentList.push(
      <Select
        id="account_type_corporate_owner_name"
        name="corporateOwnerSelection"
        required
        needsDefaultOption
        value={formik.values.corporateOwnerSelection || ''}
        errorMessage={errorText('corporateOwnerSelection')}
        error={showErrors('corporateOwnerSelection')}
        label="Corporate Owner"
        onChange={handleChange}
        onBlur={formik.handleBlur}
        options={props.corporationList}
      />
    );

    if (formik.values.corporateOwnerSelection === 'other') {
      componentList.push(
        <TextField
          name="unlistedCorporateOwnerName"
          variant="outlined"
          label="Corporate Owner Name"
          required
          errorMessage={errorText('unlistedCorporateOwnerName')}
          error={showErrors('unlistedCorporateOwnerName')}
          value={formik.values.unlistedCorporateOwnerName || ''}
          onChange={handleChange}
          onBlur={formik.handleBlur}
        />
      );
    }
  }

  const allowProgress = Object.keys(validationErrors).length === 0;

  return (
    <ResellerForm
      tab="accountType"
      loading={loading}
      location={props.location}
      allowProgress={allowProgress}
    >
      <StyledForm noValidate onSubmit={handleSubmit}>
        <Stack spacing={5.5}>
          <Section title="ACCOUNT TYPE" components={componentList} />
          <Section
            components={[
              <Button fullWidth variant="contained" color="secondary">
                CANCEL
              </Button>,
              <Button
                disabled={!allowProgress}
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                NEXT
              </Button>,
            ]}
          />
        </Stack>
      </StyledForm>
    </ResellerForm>
  );
}
